import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import NavListItem from "../NavListItem";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AssignmentIndRounded,
  CampaignRounded,
  DescriptionRounded,
  HealingRounded,
  LocalHospitalRounded,
  PsychologyRounded,
  VerifiedUserRounded,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  Typography,
} from "@mui/material";
import { CAREMAP_COLUMN_MAX_WIDTH_PX } from "../../map/careMapLayoutConstants";

/** Above CareMap fixed chrome (menu row z-index 1300) and MUI default temporary Drawer (1200). */
const MAIN_MENU_DRAWER_Z_INDEX = 1400;
/** CareMap / MSI³ primary blue — suite + footer links in the landing drawer. */
const CAREMAP_MAIN_MENU_ITEM_COLOR = "primary.main";

const MenuButton = () => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <>
      <IconButton onClick={() => setShowMenu(true)}>
        <MenuIcon />
      </IconButton>
      <MainMenu open={showMenu} close={() => setShowMenu(false)} />
    </>
  );
};

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1, 2),
  ...theme.mixins.toolbar,
}));

const MainMenu = ({ open, close, alignDrawerWithCareMapColumn = false }) => {
  const paperSx = {
    zIndex: MAIN_MENU_DRAWER_Z_INDEX,
    ...(alignDrawerWithCareMapColumn && {
      left: {
        xs: 0,
        md: `max(0px, calc((100vw - ${CAREMAP_COLUMN_MAX_WIDTH_PX}px) / 2))`,
      },
    }),
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      sx={{ zIndex: MAIN_MENU_DRAWER_Z_INDEX }}
      PaperProps={{ sx: paperSx }}
    >
      <DrawerHeader sx={{ alignItems: "center" }}>
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={close}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
      </DrawerHeader>

      <Divider />

      <List sx={{ width: 300 }}>
        <NavListItem
          icon={<HealingRounded />}
          text="Symptom Checker"
          secondary="Patients"
          link="/symptomchecker"
          itemColor={CAREMAP_MAIN_MENU_ITEM_COLOR}
        />

        <NavListItem
          icon={<AssignmentIndRounded />}
          text="MasterChart"
          secondary="Patients"
          link="/auth?role=p2"
          itemColor={CAREMAP_MAIN_MENU_ITEM_COLOR}
        />

        <NavListItem
          icon={<PsychologyRounded />}
          text="ChartMind"
          secondary="Clinicians"
          link="/chartmind"
          matchPathPrefix
          itemColor={CAREMAP_MAIN_MENU_ITEM_COLOR}
        />

        <NavListItem
          icon={<LocalHospitalRounded />}
          text="Savoir-Care"
          secondary="Facilities"
          link="/auth?role=facility"
          itemColor={CAREMAP_MAIN_MENU_ITEM_COLOR}
        />

        <NavListItem
          icon={<CampaignRounded />}
          text="Promotional Sponsorship"
          secondary="Advertisers"
          link="/auth?role=advertiser"
          itemColor={CAREMAP_MAIN_MENU_ITEM_COLOR}
        />
      </List>

      <Box sx={{ position: "absolute", bottom: 0, width: 300, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <List dense disablePadding sx={{ width: "100%" }}>
          <NavListItem
            icon={<DescriptionRounded />}
            text="Terms of Use"
            link="/terms-of-use"
            itemColor={CAREMAP_MAIN_MENU_ITEM_COLOR}
          />
          <NavListItem
            icon={<VerifiedUserRounded />}
            text="Privacy Policy"
            link="/privacy-policy"
            itemColor={CAREMAP_MAIN_MENU_ITEM_COLOR}
          />
        </List>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, mb: 2, px: 2, display: "block" }}
        >
          This site is protected by reCAPTCHA and the Google{" "}
          <Link href="https://policies.google.com/privacy">Privacy Policy</Link>{" "}
          and{" "}
          <Link href="https://policies.google.com/terms">Terms of Service</Link>{" "}
          apply.
        </Typography>
      </Box>
    </Drawer>
  );
};

export { MainMenu };
export default MenuButton;
